// People → BA Roster (BA P1.6a, `ba:profile:review`): profiles by status; a
// profile's submitted revision next to the live one with changed fields
// highlighted, who verified what and when (reviewer-only, AC2), its history,
// the review actions with per-field / per-experience "verified" ticks, and a
// preview as client from the API (AC3). Presentation only — employee-service
// fences reviewers by managing company and blocks self-review.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Check, Send, X } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { DataState } from '@/features/shared/components/ui/DataState';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  BaRosterStatus, ReviewerBaProfile, VerifiedChecks, approveAndPublishBa, approveBaRevision, getBaPreview, getBaProfile,
  listBaProfiles, publishBaProfile, requestBaChanges, unpublishBaProfile,
} from '@/services/baProfileService';
import { RevisionView } from '../components/ClientProfileView';
import { ClientPreview } from '../components/ClientPreview';
import { StatusBanner } from '../components/StatusBanner';
import { BA_REVOCATION_TEXT, FIELD_LABELS, VERIFIABLE_FIELDS, changedFields } from '../portfolio';

const FILTERS: { key: BaRosterStatus; label: string }[] = [
  { key: 'submitted', label: 'Waiting for review' },
  { key: 'changes_requested', label: 'Changes requested' },
  { key: 'approved', label: 'Approved, not published' },
  { key: 'published', label: 'In the catalogue' },
  { key: 'unpublished', label: 'Not in the catalogue' },
];

type Load = { state: 'loading' } | { state: 'ok'; rows: ReviewerBaProfile[] } | { state: 'denied' | 'unavailable' } |
  { state: 'error'; message: string };

const fail = (e: any, fallback: string): Load =>
  e?.response?.status === 403 ? { state: 'denied' }
    : e?.response?.status === 404 ? { state: 'unavailable' }
      : { state: 'error', message: getApiErrorMessage(e, fallback) };

export const BaRosterPage: React.FC = () => {
  const { t } = useTranslation('brandAmbassadors');
  const company = useAppSelector((s) => s.tenant?.current?.name) || t('company_fallback', { defaultValue: 'your company' });
  const [status, setStatus] = useState<BaRosterStatus>('submitted');
  const [list, setList] = useState<Load>({ state: 'loading' });
  const [selected, setSelected] = useState<ReviewerBaProfile | null>(null);

  const load = useCallback(() => {
    setList({ state: 'loading' });
    listBaProfiles(status)
      .then((rows) => setList({ state: 'ok', rows }))
      .catch((e) => setList(fail(e, t('errors.roster', { defaultValue: 'Could not load the roster.' }) as string)));
  }, [status, t]);
  useEffect(load, [load]);

  const open = (id: string) =>
    getBaProfile(id).then(setSelected)
      .catch((e) => toast.error(getApiErrorMessage(e, t('errors.profile', { defaultValue: 'Could not open this profile.' }) as string)));

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-h2 text-foreground">{t('roster.title', { defaultValue: 'Brand Ambassador roster' })}</h1>
        <p className="text-sm text-muted-foreground">
          {t('roster.subtitle', { defaultValue: 'Review portfolios before clients see them. Clients keep seeing the live version while an edit is reviewed.' })}
        </p>
        <p className="text-xs text-muted-foreground mt-1" data-testid="offboarding-help">
          {t('roster.offboarding_help', { defaultValue: `When a Brand Ambassador leaves, or a profile is unpublished, they are told: “${BA_REVOCATION_TEXT}” Assignment history is kept.` })}
        </p>
      </div>
      <div className="flex flex-wrap gap-2" role="tablist">
        {FILTERS.map((f) => (
          <Button key={f.key} size="sm" role="tab" aria-selected={status === f.key}
            variant={status === f.key ? 'default' : 'outline'}
            onClick={() => { setStatus(f.key); setSelected(null); }}>
            {t(`roster.filter_${f.key}`, { defaultValue: f.label })}
          </Button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <Card><CardContent className="p-0">
          {list.state === 'loading' && <DataState variant="loading" />}
          {list.state === 'denied' && <DataState variant="denied" />}
          {list.state === 'unavailable' && (
            <DataState variant="unavailable" message={t('unavailable_roster', { defaultValue: 'Brand Ambassador portfolios aren’t available for this company.' }) as string} />
          )}
          {list.state === 'error' && <DataState variant="error" message={list.message} onRetry={load} />}
          {list.state === 'ok' && list.rows.length === 0 && (
            <DataState variant="empty" title={t('roster.empty', { defaultValue: 'No profiles here.' }) as string} />
          )}
          {list.state === 'ok' && list.rows.length > 0 && (
            <ul>{list.rows.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => open(r.id)}
                  className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted ${selected?.id === r.id ? 'bg-muted' : ''}`}>
                  <p className="text-sm font-medium text-foreground">
                    {r.submitted_revision?.content.display_name || r.published_revision?.content.display_name || r.employee_name || '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">{r.employee_name}</p>
                </button>
              </li>
            ))}</ul>
          )}
        </CardContent></Card>

        {selected
          ? <ProfileReview key={`${selected.id}:${selected.submitted_revision?.id ?? ''}`} profile={selected} company={company}
              onChange={(p) => { setSelected(p); load(); }} />
          : <Card><CardContent><DataState variant="empty" title={t('roster.pick', { defaultValue: 'Pick a profile to review.' }) as string} /></CardContent></Card>}
      </div>
    </div>
  );
};

export const ProfileReview: React.FC<{
  profile: ReviewerBaProfile; company: string; onChange: (p: ReviewerBaProfile) => void;
}> = ({ profile: p, company, onChange }) => {
  const { t } = useTranslation('brandAmbassadors');
  const prompt = usePrompt();
  const [busy, setBusy] = useState(false);
  const [previewOf, setPreviewOf] = useState<string | null>(null); // a revision id, or 'published'
  const sub = p.submitted_revision;
  // AC2: what the reviewer checked. Carried marks start ticked; she may untick them.
  const carried = (sub?.verification_marks ?? []).filter((m) => m.carried);
  const [checks, setChecks] = useState<VerifiedChecks>(() => ({
    fields: carried.filter((m) => m.field !== 'experience').map((m) => m.field),
    experience: carried.filter((m) => m.field === 'experience' && m.item != null).map((m) => m.item as number),
  }));
  const toggle = (kind: 'fields' | 'experience', v: string | number) =>
    setChecks((c) => ({ ...c, [kind]: (c[kind] as any[]).includes(v) ? (c[kind] as any[]).filter((x) => x !== v) : [...c[kind], v] }));
  const liveRev = p.publication.status === 'published' ? p.published_revision : null;
  const changed = sub ? changedFields(sub.content, liveRev?.content) : [];
  // The newest approved revision (history is newest first) — publishable unless
  // it's already the live one (a live revision keeps status "approved").
  const latestApproved = p.revisions.find((r) => r.status === 'approved');
  const approvedId = latestApproved && latestApproved.id !== (liveRev?.id ?? null) ? latestApproved.id : undefined;

  const run = async (fn: () => Promise<ReviewerBaProfile>, ok: string) => {
    setBusy(true);
    try { onChange(await fn()); toast.success(ok); } catch (e: any) {
      // Approve & publish is two calls: say the approval stood when only the publish failed.
      const afterApprove = e?.step === 'publish';
      toast.error(t(afterApprove ? 'errors.publish_after_approve' : 'errors.reason', {
        defaultValue: afterApprove ? 'Approved, but not published: {{msg}}' : '{{msg}}',
        msg: getApiErrorMessage(e, t('errors.action', { defaultValue: 'That didn’t work. Try again.' }) as string),
      }) as string);
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{p.employee_name || '—'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <StatusBanner profile={p} />

        <div className="flex flex-wrap gap-2">
          {sub && (
            <>
              <Button disabled={busy} onClick={() => run(() => approveBaRevision(p.id, sub.id, checks), t('toast.approved', { defaultValue: 'Approved.' }) as string)}>
                <Check className="w-4 h-4 mr-1" />{t('actions.approve', { defaultValue: 'Approve' })}
              </Button>
              <Button disabled={busy} onClick={() => run(() => approveAndPublishBa(p.id, sub.id, checks), t('toast.published', { defaultValue: 'Approved and published.' }) as string)}>
                <Send className="w-4 h-4 mr-1" />{t('actions.approve_publish', { defaultValue: 'Approve & publish' })}
              </Button>
              <Button variant="outline" className="text-danger" disabled={busy} onClick={async () => {
                const reason = await prompt({
                  title: t('actions.request_changes', { defaultValue: 'Request changes' }) as string,
                  label: t('review.reason_label', { defaultValue: 'What should the BA change?' }) as string,
                  confirmLabel: t('actions.request_changes', { defaultValue: 'Request changes' }) as string,
                  required: true,
                });
                if (!reason || !reason.trim()) return;
                await run(() => requestBaChanges(p.id, sub.id, reason.trim()), t('toast.changes_requested', { defaultValue: 'Changes requested.' }) as string);
              }}>
                <X className="w-4 h-4 mr-1" />{t('actions.request_changes', { defaultValue: 'Request changes' })}
              </Button>
            </>
          )}
          {!sub && approvedId && (
            <Button disabled={busy} onClick={() => run(() => publishBaProfile(p.id, approvedId), t('toast.published_only', { defaultValue: 'Published.' }) as string)}>
              <Send className="w-4 h-4 mr-1" />{t('actions.publish', { defaultValue: 'Publish' })}
            </Button>
          )}
          {liveRev && (
            <Button variant="outline" className="text-danger" disabled={busy} onClick={async () => {
              const reason = await prompt({
                title: t('actions.unpublish', { defaultValue: 'Unpublish' }) as string,
                label: t('review.unpublish_label', { defaultValue: 'Why? (kept for your records; clients aren’t told)' }) as string,
                confirmLabel: t('actions.unpublish', { defaultValue: 'Unpublish' }) as string,
                danger: true,
              });
              if (reason === null || reason === undefined) return;
              await run(() => unpublishBaProfile(p.id, reason.trim()), t('toast.unpublished', { defaultValue: 'Removed from the catalogue.' }) as string);
            }}>
              {t('actions.unpublish', { defaultValue: 'Unpublish' })}
            </Button>
          )}
        </div>

        {sub && (
          <fieldset className="rounded-md border border-border p-3 space-y-2" data-testid="verify-checks">
            <legend className="px-1 text-sm font-semibold text-foreground">
              {t('review.verify_title', { defaultValue: 'What {{company}} checked', company })}
            </legend>
            <p className="text-xs text-muted-foreground">
              {t('review.verify_hint', { defaultValue: 'Tick only what you checked. Introduction and display name are the BA’s own words and can’t be verified.' })}
            </p>
            <div className="flex flex-wrap gap-4">
              {VERIFIABLE_FIELDS.map((f) => (
                <label key={f} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={checks.fields.includes(f)} onCheckedChange={() => toggle('fields', f)}
                    aria-label={t(`fields.${f}`, { defaultValue: FIELD_LABELS[f] }) as string} />
                  {t(`fields.${f}`, { defaultValue: FIELD_LABELS[f] })}
                </label>
              ))}
            </div>
            {(sub.content.experience ?? []).length > 0 && (
              <div className="space-y-1">
                {(sub.content.experience ?? []).map((e, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={checks.experience.includes(i)} onCheckedChange={() => toggle('experience', i)}
                      aria-label={e.title} />
                    {e.title}{e.year ? ` · ${e.year}` : ''}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}

        <div className="grid gap-4 xl:grid-cols-2">
          {sub && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                {t('review.submitted', { defaultValue: 'Submitted: version {{n}}', n: sub.revision_no })}
                {changed.length > 0 && (
                  <Badge variant="info" className="ml-2">{t('review.changed', { defaultValue: '{{count}} changed', count: changed.length })}</Badge>
                )}
              </h3>
              <RevisionView content={sub.content} marks={sub.verification_marks} company={company} highlight={changed} />
              <Button size="sm" variant="outline" onClick={() => setPreviewOf(sub.id)}>
                {t('preview.as_client', { defaultValue: 'Preview as client' })}
              </Button>
            </div>
          )}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground">
              {liveRev
                ? t('review.live', { defaultValue: 'Live: version {{n}}', n: liveRev.revision_no })
                : t('review.none_live', { defaultValue: 'Nothing live' })}
            </h3>
            {liveRev && <RevisionView content={liveRev.content} marks={liveRev.verification_marks} company={company} />}
            {liveRev && (
              <Button size="sm" variant="outline" onClick={() => setPreviewOf('published')}>
                {t('preview.as_client', { defaultValue: 'Preview as client' })}
              </Button>
            )}
          </div>
        </div>

        {previewOf && (
          <div className="space-y-2" data-testid="reviewer-preview">
            <h3 className="text-sm font-semibold text-foreground">{t('preview.title', { defaultValue: 'Preview as client' })}</h3>
            <ClientPreview company={company} load={() => getBaPreview(p.id, previewOf)} reloadKey={`${previewOf}:${p.version}`} />
          </div>
        )}

        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('review.history', { defaultValue: 'Review history' })}</h3>
          {p.revisions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('review.no_history', { defaultValue: 'No submissions yet.' })}</p>
          ) : (
            <ul className="text-sm space-y-1" data-testid="history">
              {p.revisions.map((r) => (
                <li key={r.id} className="flex gap-2">
                  <span className="tabular-nums text-muted-foreground">v{r.revision_no}</span>
                  <span className="text-foreground">{t(`revision_status.${r.status}`, { defaultValue: r.status.replace(/_/g, ' ') })}</span>
                  {r.submitted_at && <span className="text-muted-foreground">{new Date(r.submitted_at).toLocaleDateString()}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BaRosterPage;
